import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);
  const [courseCategories, setCourseCategories] = useState(["surf", "skate"]);
  const [bizumRecipients, setBizumRecipients] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/course-categories`, { withCredentials: true });
      if (Array.isArray(data)) setCourseCategories(data);
    } catch { /* use default */ }
  }, []);

  const fetchBizumRecipients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bizum-recipients`, { withCredentials: true });
      if (Array.isArray(data)) setBizumRecipients(data);
    } catch { /* use default */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      await Promise.all([fetchCategories(), fetchBizumRecipients()]);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [fetchCategories, fetchBizumRecipients]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      await Promise.all([fetchCategories(), fetchBizumRecipients()]);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(false);
    setCourseCategories(["surf", "skate"]);
    setBizumRecipients([]);
  };

  const updateCourseCategories = (cats) => setCourseCategories(cats);
  const updateBizumRecipients = (recipients) => setBizumRecipients(recipients);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, courseCategories, updateCourseCategories, bizumRecipients, updateBizumRecipients }}>
      {children}
    </AuthContext.Provider>
  );
}
